import React, { useState } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography as MuiTypography,
  LinearProgress,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { useMutation, useQuery } from "@apollo/client";
import { navigate, RouteComponentProps } from "@reach/router";
import queryString from "query-string";
import { gql } from "@apollo/client";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LockIcon from "@material-ui/icons/Lock";
import ErrorIcon from "@material-ui/icons/Error";

// GraphQL Queries and Mutations
const VALIDATE_TOKEN = gql`
  query ValidatePasswordToken($token: String!) {
    validatePasswordToken(token: $token) {
      valid
      email
    }
  }
`;

const SETUP_COACH_PASSWORD = gql`
  mutation SetupCoachPassword($password: String!, $token: String!) {
    setupCoachPassword(password: $password, token: $token) {
      success
      error
      token
    }
  }
`;

// Styled Components
const PageContainer = styled("div")(({ theme }: IStyledArguments) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: theme.palette.extra.backgroundGrey,
  padding: theme.spacing(3),
}));

const ContentCard = styled(Card)(({ theme }: IStyledArguments) => ({
  maxWidth: 480,
  width: "100%",
  padding: theme.spacing(4),
  borderRadius: "20px",
  boxShadow: "0 4px 16px rgba(16, 21, 26, 0.1)",
}));

const LogoContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  marginBottom: theme.spacing(3),
}));

const IconWrapper = styled(Box)(({ theme }: IStyledArguments) => ({
  width: 80,
  height: 80,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(2),
  "& svg": {
    fontSize: "2.5rem",
    color: "white",
  },
}));

const StyledTextField = styled(TextField)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(2),
}));

const PasswordStrengthBar = styled(Box)(
  ({ theme, strength }: IStyledArguments & { strength: number }) => ({
    height: 4,
    borderRadius: 2,
    background: theme.palette.grey[200],
    marginTop: theme.spacing(1),
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      height: "100%",
      width: `${strength}%`,
      background:
        strength < 40
          ? theme.palette.error.main
          : strength < 70
          ? theme.palette.warning.main
          : theme.palette.success.main,
      transition: "width 0.3s ease",
    },
  })
);

const PasswordRequirement = styled(Box)(
  ({ theme, met }: IStyledArguments & { met: boolean }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    color: met ? theme.palette.success.main : theme.palette.text.secondary,
    "& svg": {
      fontSize: "1rem",
    },
  })
);

const SubmitButton = styled(Button)(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(1.5),
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "12px",
  background: theme.palette.primary.main,
  "&:hover": {
    background: theme.palette.primary.dark,
  },
}));

const ErrorAlert = styled(Box)(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: "8px",
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.dark,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const ErrorCard = styled(Card)(({ theme }: IStyledArguments) => ({
  maxWidth: 480,
  width: "100%",
  padding: theme.spacing(4),
  borderRadius: "20px",
  boxShadow: "0 4px 16px rgba(16, 21, 26, 0.1)",
  textAlign: "center",
}));

// Helper function to calculate password strength
const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 6) strength += 25;
  if (password.length >= 8) strength += 15;
  if (password.length >= 12) strength += 10;
  if (/[a-z]/.test(password)) strength += 10;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
  return Math.min(strength, 100);
};

type PageSetPasswordProps = RouteComponentProps;

const PageSetPassword: React.FC<PageSetPasswordProps> = ({ location }) => {
  const params = queryString.parse(location?.search || "");
  const token = (params.token as string) || "";

  console.log("🔑 PageSetPassword - Token from URL:", token);
  console.log("🔑 PageSetPassword - Full URL:", location?.search);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");

  // Validate token on page load
  const { data: tokenData, loading: validating, error: tokenError } = useQuery(
    VALIDATE_TOKEN,
    {
      variables: { token },
      skip: !token,
      onCompleted: (data) => {
        console.log("✅ ValidateToken Response:", data);
      },
      onError: (error) => {
        console.error("❌ ValidateToken Error:", error);
      },
    }
  );

  // Setup password mutation
  const [setupCoachPassword, { loading: submitting }] = useMutation(
    SETUP_COACH_PASSWORD,
    {
      onCompleted: (data) => {
        if (data.setupCoachPassword.success && data.setupCoachPassword.token) {
          // Store the authentication token
          localStorage.setItem("authToken", data.setupCoachPassword.token);
          // Redirect to dashboard
          navigate("/dashboard");
        } else {
          setError(data.setupCoachPassword.error || "Failed to set password");
        }
      },
      onError: (err) => {
        setError(err.message);
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setupCoachPassword({
      variables: { password, token },
    });
  };

  const passwordStrength = calculatePasswordStrength(password);

  const requirements = {
    minLength: password.length >= 6,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };

  const isPasswordValid = requirements.minLength;
  const isFormValid = isPasswordValid && password === confirmPassword;

  // Loading state
  if (!token) {
    return (
      <PageContainer>
        <ErrorCard>
          <IconWrapper style={{ background: "#f44336" }}>
            <LockIcon />
          </IconWrapper>
          <MuiTypography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
            Invalid Link
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            This password setup link is invalid or malformed.
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            Please contact your administrator for a new invitation.
          </MuiTypography>
        </ErrorCard>
      </PageContainer>
    );
  }

  if (validating) {
    return (
      <PageContainer>
        <ContentCard>
          <LogoContainer>
            <IconWrapper>
              <LockIcon />
            </IconWrapper>
            <MuiTypography variant="h5" style={{ fontWeight: 600 }}>
              Validating...
            </MuiTypography>
          </LogoContainer>
          <LinearProgress />
        </ContentCard>
      </PageContainer>
    );
  }

  // If token is invalid or there's an error
  if (tokenError || !tokenData?.validatePasswordToken?.valid) {
    return (
      <PageContainer>
        <ErrorCard>
          <IconWrapper style={{ background: "#f44336" }}>
            <LockIcon />
          </IconWrapper>
          <MuiTypography variant="h5" gutterBottom style={{ fontWeight: 600 }}>
            Link Expired or Invalid
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            This password setup link is no longer valid or has expired.
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
            Please contact your administrator for a new invitation.
          </MuiTypography>
        </ErrorCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentCard>
        <LogoContainer>
          <IconWrapper>
            <LockIcon />
          </IconWrapper>
          <MuiTypography variant="h5" style={{ fontWeight: 600, marginBottom: 8 }}>
            Set Your Password
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            Create a password to access your coach account
          </MuiTypography>
        </LogoContainer>

        <form onSubmit={handleSubmit}>
          {/* Email Field (Read-only) */}
          <StyledTextField
            fullWidth
            label="Email"
            value={tokenData.validatePasswordToken.email}
            disabled
            variant="outlined"
          />

          {/* Password Field */}
          <StyledTextField
            fullWidth
            label="New Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Password Strength Indicator */}
          {password && (
            <>
              <PasswordStrengthBar strength={passwordStrength} />
              <MuiTypography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 4 }}
              >
                Password strength:{" "}
                {passwordStrength < 40
                  ? "Weak"
                  : passwordStrength < 70
                  ? "Medium"
                  : "Strong"}
              </MuiTypography>

              {/* Password Requirements */}
              <Box mt={2}>
                <PasswordRequirement met={requirements.minLength}>
                  <CheckCircleIcon />
                  <MuiTypography variant="caption">
                    At least 6 characters
                  </MuiTypography>
                </PasswordRequirement>
                <PasswordRequirement met={requirements.hasUpperCase}>
                  <CheckCircleIcon />
                  <MuiTypography variant="caption">
                    One uppercase letter (optional)
                  </MuiTypography>
                </PasswordRequirement>
                <PasswordRequirement met={requirements.hasLowerCase}>
                  <CheckCircleIcon />
                  <MuiTypography variant="caption">
                    One lowercase letter (optional)
                  </MuiTypography>
                </PasswordRequirement>
                <PasswordRequirement met={requirements.hasNumber}>
                  <CheckCircleIcon />
                  <MuiTypography variant="caption">
                    One number (optional)
                  </MuiTypography>
                </PasswordRequirement>
              </Box>
            </>
          )}

          {/* Confirm Password Field */}
          <StyledTextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            variant="outlined"
            required
            error={confirmPassword.length > 0 && password !== confirmPassword}
            helperText={
              confirmPassword.length > 0 && password !== confirmPassword
                ? "Passwords do not match"
                : ""
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Error Alert */}
          {error && (
            <ErrorAlert>
              <ErrorIcon />
              <MuiTypography variant="body2">{error}</MuiTypography>
            </ErrorAlert>
          )}

          {/* Submit Button */}
          <SubmitButton
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={!isFormValid || submitting}
          >
            {submitting ? "Setting Password..." : "Set Password & Login"}
          </SubmitButton>
        </form>
      </ContentCard>
    </PageContainer>
  );
};

export default PageSetPassword;
